import { useState, useEffect, useCallback, useRef } from "react";
import type { Directory, Instance, Wasmer } from "@wasmer/sdk";
import type { Terminal } from "@xterm/xterm";
import { useEditorState } from "./EditorStateContext";
import type * as Y from "yjs";
import {
	type FileSystem,
	FileType,
	type YFileSystemObject,
} from "@/utils/yjs/FileSystem";
// @ts-ignore
import WasmModule from "@wasmer/sdk/wasm?url";
import { useEditorStateStore } from "./editorStateStore";
import { T } from "node_modules/better-auth/dist/auth-o42_VXAs";
import { TemplateType } from "@/utils/yjs/constants";

export async function syncFileSystemToWasmer(
	targetDir: Directory,
	fileSystem: FileSystem,
): Promise<void> {
	// Get all keys from the file system and sort them by depth (shallow to deep)
	const allKeys = Array.from(fileSystem.fsDoc.keys()).sort(
		(a, b) => a.split("/").length - b.split("/").length,
	);

	// Create directories (skip root which already exists)
	for (const key of allKeys) {
		try {
			if (fileSystem.getType(key) === FileType.Directory && key !== "/") {
				await targetDir.createDir(key);
			}
		} catch (error) {
			console.error(`Failed to create directory ${key}:`, error);
		}
	}

	// Write all files into the target directory
	for (const key of allKeys) {
		try {
			if (fileSystem.getType(key) === FileType.File) {
				const fileObj = fileSystem.readFile(key);
				const contents = fileObj.contents?.toString() || "";
				await targetDir.writeFile(key, contents);
			}
		} catch (error) {
			console.error(`Failed to write file ${key}:`, error);
		}
	}
}

interface WasmerCommandOptions {
	env?: Record<string, string>;
	// Add other options here as needed, e.g.,
	// preopens?: Record<string, string>;
}

interface WasmerCommand {
	id: string;
	command: string;
	args: string[];
	options?: WasmerCommandOptions;
}

interface UseWasmerResult {
	instance: Instance | null;
	isLoading: boolean;
	error: Error | null;
	runCommand: (command: WasmerCommand, terminal: Terminal) => Promise<number>;
	setFileContents: (path: string, newContents: string) => Promise<void>;
}

const typeToEngine: Record<TemplateType, string> = {
	[TemplateType.JavaScript]: "wasmer/winterjs",
	[TemplateType.Python]: "python/python",
};

// Initialize the home directory globally
export let wasmerHomeDir: Directory;

export function useWasmer(): UseWasmerResult {
	const [instance, setInstance] = useState<Instance | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);

	const pkgRef = useRef<Wasmer | null>(null);

	const { yfs, template } = useEditorStateStore();

	useEffect(() => {
		const initializeWasmer = async () => {
			setIsLoading(true);
			setError(null);
			try {
				const { Wasmer, init, Directory } = await import("@wasmer/sdk");
				await init({ module: WasmModule });
				// Initialize the home directory
				wasmerHomeDir = new Directory();
				// Sync the initial file system
				if (yfs) {
					await syncFileSystemToWasmer(wasmerHomeDir, yfs);
				}
				console.log("template", template);
				console.log("using template", typeToEngine[template.type]);
				pkgRef.current = await Wasmer.fromRegistry(typeToEngine[template.type]);
			} catch (err) {
				setError(err instanceof Error ? err : new Error(String(err)));
			} finally {
				setIsLoading(false);
			}
		};

		initializeWasmer();
	}, [yfs, template]);

	const runCommand = useCallback(
		async (command: WasmerCommand, terminal: Terminal): Promise<number> => {
			if (!pkgRef.current || !wasmerHomeDir || !yfs) {
				throw new Error("Wasmer not initialized.");
			}

			console.log("running command", command);

			setIsLoading(true);
			const fullArgs = [command.command, ...command.args];

			// Make sure file system is fully synced
			await syncFileSystemToWasmer(wasmerHomeDir, yfs);

			const wasmerInstance = await pkgRef.current.entrypoint!.run({
				args: fullArgs,
				mount: { "/": wasmerHomeDir },
				cwd: "/",
				env: command.options?.env,
			});
			setInstance(wasmerInstance);

			const decoder = new TextDecoder();

			if (wasmerInstance.stdout) {
				// Create a writable stream that writes chunks to the terminal
				const stdoutStream = new WritableStream({
					write(chunk) {
						terminal.write(chunk);
					},
				});
				wasmerInstance.stdout.pipeTo(stdoutStream);
			}

			// Also pipe stderr
			if (wasmerInstance.stderr) {
				// Create a writable stream that writes chunks to the terminal
				const stderrStream = new WritableStream({
					write(chunk) {
						terminal.write(chunk);
					},
				});
				wasmerInstance.stderr.pipeTo(stderrStream);
			}

			// Optionally, you can wait for the command to finish and return its exit code.
			const result = await wasmerInstance.wait();
			setIsLoading(false);
			return result.code;
		},
		[yfs],
	);

	const setFileContents = useCallback(
		async (path: string, newContents: string): Promise<void> => {
			if (!wasmerHomeDir) {
				throw new Error("Wasmer not initialized.");
			}
			await wasmerHomeDir.writeFile(path, newContents);
		},
		[],
	);

	// Listens for file-system changes like file creation, deletion, and modification
	useEffect(() => {
		if (!yfs || !wasmerHomeDir) return;

		const observer = async (event: Y.YMapEvent<YFileSystemObject>) => {
			for (const key of event.keysChanged) {
				try {
					const type = yfs.getType(key);
					if (type === FileType.File && wasmerHomeDir) {
						const newValue = yfs.readFile(key).contents?.toString();
						// Update or create the file in the Wasmer Directory
						await wasmerHomeDir.writeFile(key, newValue ?? "");
					} else if (type === FileType.Directory) {
						// Create or update the directory
						await wasmerHomeDir.createDir(key);
					}
				} catch (error) {
					// If the error is not about a missing file or directory, rethrow it.
					if (
						!(error instanceof Error) ||
						!error.message.includes("File or Directory not found")
					) {
						throw error;
					}

					// Remove the file/directory from the Wasmer file system
					await wasmerHomeDir.removeFile(key);
					// Also remove it from the yjs file system
					yfs.rm(key);
				}
			}
		};

		yfs.fsDoc.observe(observer);

		return () => {
			yfs.fsDoc.unobserve(observer);
		};
	}, [yfs]);

	return {
		instance,
		isLoading,
		error,
		runCommand,
		setFileContents,
	};
}
